import Link from 'next/link';
import { FC, forwardRef, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { AnalyticsEventName, EventWithPayload } from '@/api/analytics';
import { Avatar } from '@/components/avatar';
import { Button } from '@/components/buttons';
import {
  BonanzaPromoDashboardDesktopPanel,
  DashboardPanelMobile,
  LoyaltyDashboardDesktopPanel,
  TopSpotGameDashboardDesktopPanel,
} from '@/components/general/Header/DashboardPanel';
import { ACCOUNT_LINKS } from '@/components/general/Header/UserBlock/helpers';
import { SelectDeposit } from '@/components/SelectDeposit';
import { NamesSteps } from '@/helpers/auth';
import { ThemeMode } from '@/helpers/enums';
import { Modals } from '@/helpers/modals';
import { COMMON_VIP_GROUPS, USER_GROUP } from '@/helpers/user';
import { useAnalytics } from '@/hooks/analytics';
import useEstonia from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { useComponentVisible } from '@/hooks/visibility';
import { useCMSAvatar } from '@/modules/avatar';
import { sendOtherSignOutClickEvent } from '@/modules/googleAnalytics/events/otherEvents';
import { usePromotionsAvailability } from '@/modules/promotions';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import {
  STORAGE,
  useCommonStore,
  useLocalStorageStore,
  useModalsStore,
} from '@/store';

import {
  ScAccountAvatar,
  ScAccountBlock,
  ScAccountButton,
  ScAccountDropDown,
  ScAccountInfo,
  ScDepositPanel,
  ScDepositPanelProps,
  ScUserWelcome,
} from './styled';

const AccountBlock: FC = () => {
  const { images } = useTheme();

  const resetUser = useCommonStore.use.resetUser();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const open = useModalsStore.use.open();

  const { sendEventToAnalytics } = useAnalytics();
  const { avatarImg } = useCMSAvatar();

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const toggleOpenAccountInfo = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleLogout = () => {
    sendOtherSignOutClickEvent();
    useLocalStorageStore.getState().remove(STORAGE.HAS_SHOWN_LIMIT_MODAL);
    sendEventToAnalytics({
      name: AnalyticsEventName.HEADER__SIGN_OUT__BUTTON__CLICK,
    });
    useCommonStore.setState({
      currentRegistrationStep: NamesSteps.PRIMARY_INFORMATION,
      isAdditionalUserDataForm: false,
    });
    resetUser();
  };

  const handleOpenAvatarModal = () => {
    toggleOpenAccountInfo();
    open(Modals.ChooseAvatar);
  };

  return (
    <ScAccountBlock>
      <ScAccountButton
        ref={openerRef}
        active={isComponentVisible}
        onClick={toggleOpenAccountInfo}
      >
        <ScAccountAvatar>
          <Avatar {...avatarImg} />
        </ScAccountAvatar>

        <div className="arrow">
          <images.arrowDownGrayIcon.component />
        </div>
      </ScAccountButton>
      {isComponentVisible &&
        (!isMobileDevice ? (
          <AccountDropDown
            ref={ref}
            onResetUser={handleLogout}
            onOpenAvatarModal={handleOpenAvatarModal}
            toggleOpenAccountInfo={toggleOpenAccountInfo}
          />
        ) : (
          <AccountInfo onClose={toggleOpenAccountInfo} />
        ))}
    </ScAccountBlock>
  );
};

const UserWelcome = () => {
  const { t } = useTranslation('common');
  const user = useCommonStore.use.user();
  const isSuperVip = useCommonStore.use.isSuperVip();

  const vipType = useMemo(
    () =>
      user?.statuses?.find(({ id }) =>
        COMMON_VIP_GROUPS.includes(id as USER_GROUP)
      )?.id,
    [user]
  );

  if (!user) return null;

  const { firstName, email } = user;

  return (
    <ScUserWelcome>
      <div className="welcome">
        <p>{t('greeting', { name: firstName })}</p>
        {vipType && (
          <div className={`vip-label ${isSuperVip ? 'SUPER' : vipType}`}>
            VIP
          </div>
        )}
      </div>
      <p className="email">{email}</p>
    </ScUserWelcome>
  );
};

interface AccountDropDownProps {
  onResetUser: () => void;
  onOpenAvatarModal: () => void;
  toggleOpenAccountInfo: () => void;
}

const AccountDropDown = forwardRef<HTMLDivElement, AccountDropDownProps>(
  ({ onResetUser, onOpenAvatarModal, toggleOpenAccountInfo }, ref) => {
    const { t } = useTranslation('common');
    const user = useCommonStore.use.user();

    const { generateLink } = useNavigation();

    return (
      <ScAccountDropDown ref={ref}>
        <UserWelcome />
        {user && <div className="divider" />}

        <ul>
          {ACCOUNT_LINKS.map(({ name, link }) => (
            <li key={name}>
              <Link href={generateLink(link)} onClick={toggleOpenAccountInfo}>
                {t(`accountLinks.${name}`)}
              </Link>
            </li>
          ))}
          <li>
            <button onClick={onOpenAvatarModal}>
              {t('accountLinks.chooseAvatar')}
            </button>
          </li>
          <li>
            <button onClick={onResetUser}>{t('button.signOut')}</button>
          </li>
        </ul>
      </ScAccountDropDown>
    );
  }
);

interface AccountInfoProps {
  onClose: () => void;
}

export const AccountInfo = ({
  onClose,
}: PropsWithChildren<AccountInfoProps>) => {
  const open = useModalsStore.use.open();
  const { avatarImg } = useCMSAvatar();

  return (
    <ScAccountInfo>
      <div className="user-info">
        <ScAccountAvatar isMobile onClick={() => open(Modals.ChooseAvatar)}>
          <Avatar {...avatarImg} />
        </ScAccountAvatar>
        <UserWelcome />
      </div>

      <DashboardPanelMobile onLinkClick={onClose} />
    </ScAccountInfo>
  );
};

export const UserPanel = () => {
  const { topSpotGameStatus, isTopSpotGameAvailable } = useCommonStore(
    useShallow((state) => ({
      topSpotGameStatus: state.topSpotGameStatus,
      isTopSpotGameAvailable: state.isTopSpotGameAvailable,
    }))
  );
  const { isEstonia } = useEstonia();
  const { isMobileDevice } = useServer();

  const { isAnyPromoAvailable, isBonanzaPromoAvailable } =
    usePromotionsAvailability();

  return (
    <>
      {isBonanzaPromoAvailable && <BonanzaPromoDashboardDesktopPanel />}
      {topSpotGameStatus && isTopSpotGameAvailable && (
        <TopSpotGameDashboardDesktopPanel />
      )}
      {!isEstonia && !isAnyPromoAvailable && <LoyaltyDashboardDesktopPanel />}
      <DepositPanel />
      {!isMobileDevice && <AccountBlock />}
    </>
  );
};

export const DepositPanel: FC<
  ScDepositPanelProps & {
    boldValue?: boolean;
    analyticsEvent?: EventWithPayload;
  }
> = ({
  mobile,
  noPadding,
  analyticsEvent,
  reducedSizeOnSmallScreen,
  ...props
}) => {
  const { t } = useTranslation('common');
  const open = useModalsStore.use.open();

  const { sendEventToAnalytics } = useAnalytics();

  const handleDepositClick = () => {
    sendEventToAnalytics(
      analyticsEvent ?? {
        name: AnalyticsEventName.DEPOSIT_TOP,
      }
    );
    open(Modals.Deposit);
  };

  return (
    <ScDepositPanel
      mobile={mobile}
      blackBg={props.blackBg}
      noPadding={noPadding}
      reducedSizeOnSmallScreen={reducedSizeOnSmallScreen}
    >
      <SelectDeposit
        transparent
        isInputAddon
        mode={ThemeMode.Dark}
        inset="calc(100% + 12px) 0 auto 0"
        reducedSizeOnSmallScreen={reducedSizeOnSmallScreen}
        {...props}
      />
      <div className="cashier-button">
        <Button secondary small onClick={handleDepositClick}>
          {t('button.deposit')}
        </Button>
      </div>
    </ScDepositPanel>
  );
};
